import { NurseLayout } from "@/components/layout/nurse-layout";
import { useListConditions } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Link, useLocation } from "wouter";
import { Search, ChevronRight, AlertTriangle, Zap } from "lucide-react";
import { useMemo } from "react";
import { useSearchParam } from "@/hooks/use-search-param";
import { useScrollRestore } from "@/hooks/use-scroll-restore";
import { Highlight } from "@/components/ui/highlight";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CONDITION_TYPES = [
  { value: "", label: "All" },
  { value: "inflammatory_arthritis", label: "Inflammatory Arthritis" },
  { value: "connective_tissue", label: "Connective Tissue Disorders" },
  { value: "crystal_arthropathy", label: "Crystal Arthropathy" },
  { value: "vasculitis", label: "Vasculitis" },
  { value: "other", label: "Other" },
];

const typeColors: Record<string, string> = {
  inflammatory_arthritis: "bg-blue-50 text-blue-700 border-blue-200",
  connective_tissue: "bg-purple-50 text-purple-700 border-purple-200",
  crystal_arthropathy: "bg-amber-50 text-amber-700 border-amber-200",
  vasculitis: "bg-rose-50 text-rose-700 border-rose-200",
  other: "bg-slate-50 text-slate-700 border-slate-200",
};

const typeHeaderColors: Record<string, string> = {
  inflammatory_arthritis: "border-blue-200 bg-blue-50",
  connective_tissue: "border-purple-200 bg-purple-50",
  crystal_arthropathy: "border-amber-200 bg-amber-50",
  vasculitis: "border-rose-200 bg-rose-50",
  other: "border-slate-200 bg-slate-50",
};

const typeTextColors: Record<string, string> = {
  inflammatory_arthritis: "text-blue-800",
  connective_tissue: "text-purple-800",
  crystal_arthropathy: "text-amber-800",
  vasculitis: "text-rose-800",
  other: "text-slate-700",
};

const SORT_OPTIONS = [
  { value: "az", label: "A–Z" },
  { value: "za", label: "Z–A" },
  { value: "recent", label: "Recently added" },
];

export default function NurseConditions() {
  useScrollRestore("nurse-conditions");
  const [, setLocation] = useLocation();
  const [search, setSearch] = useSearchParam("q", "");
  const [activeType, setActiveType] = useSearchParam("type", "", { storageKey: "nurse-conditions:type" });
  const [sort, setSort] = useSearchParam("sort", "az", { storageKey: "nurse-conditions:sort" });

  const { data: conditions, isLoading } = useListConditions({ audience: "nurse" });

  const filtered = useMemo(() => {
    const base = conditions?.filter(c => {
      const effectiveType = c.conditionType ?? "other";
      const matchesType = !activeType || effectiveType === activeType;
      const q = search.toLowerCase();
      const matchesSearch = !search ||
        c.name.toLowerCase().includes(q) ||
        (c.simpleName ?? "").toLowerCase().includes(q) ||
        (c.nurseDescription ?? "").toLowerCase().includes(q);
      return matchesType && matchesSearch;
    });
    if (!base) return base;
    if (sort === "za") return [...base].sort((a, b) => b.name.localeCompare(a.name));
    if (sort === "recent") return [...base].sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime());
    return [...base].sort((a, b) => a.name.localeCompare(b.name));
  }, [conditions, activeType, search, sort]);

  const grouped = useMemo(() => {
    if (!filtered) return [];
    const typesToShow = activeType
      ? CONDITION_TYPES.filter(t => t.value === activeType)
      : CONDITION_TYPES.filter(t => t.value !== "");
    return typesToShow
      .map(t => ({
        ...t,
        items: filtered.filter(c => (c.conditionType ?? "other") === t.value),
      }))
      .filter(g => g.items.length > 0);
  }, [filtered, activeType]);

  const resultCount = filtered?.length ?? 0;

  if (isLoading) return <NurseLayout title="Conditions Reference"><LoadingScreen /></NurseLayout>;

  return (
    <NurseLayout title="Conditions Reference">
      <div className="mb-6">
        <p className="text-slate-600 mb-6">
          Clinical reference for rheumatic diseases — aetiopathogenesis, clinical features, investigations, and management.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search conditions…"
              className="pl-9"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-40 shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex flex-wrap gap-2">
            {CONDITION_TYPES.map(t => {
              const count = t.value === ""
                ? (conditions?.length ?? 0)
                : (conditions?.filter(c => (c.conditionType ?? "other") === t.value).length ?? 0);
              return (
                <button
                  key={t.value}
                  onClick={() => setActiveType(t.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all flex items-center gap-1.5 ${
                    activeType === t.value
                      ? "bg-teal-700 text-white border-teal-700 shadow-sm"
                      : "bg-white text-slate-600 border-slate-200 hover:border-teal-400"
                  }`}
                >
                  {t.label}
                  <span className={`text-xs rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center leading-none ${
                    activeType === t.value ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-500"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <p className={`mt-3 text-sm ${resultCount === 0 ? "text-rose-500" : "text-slate-500"}`}>
        {resultCount === 0 ? "No conditions found" : `Showing ${resultCount} ${resultCount === 1 ? "condition" : "conditions"}`}
      </p>

      {grouped.length === 0 && (
        <div className="py-16 text-center text-slate-500 bg-white rounded-2xl border border-slate-200">
          No conditions found matching your filters.
        </div>
      )}

      <div className="space-y-10">
        {grouped.map(group => (
          <section key={group.value}>
            <div className={`flex items-center justify-between px-4 py-2.5 rounded-xl border mb-4 ${typeHeaderColors[group.value] ?? typeHeaderColors.other}`}>
              <h2 className={`text-sm font-bold uppercase tracking-wider ${typeTextColors[group.value] ?? typeTextColors.other}`}>
                {group.label}
              </h2>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full bg-white/70 border ${typeColors[group.value] ?? typeColors.other}`}>
                {group.items.length} {group.items.length === 1 ? "condition" : "conditions"}
              </span>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {group.items.map(c => {
                const redFlagCount = c.redFlags?.length ?? 0;
                const hasEmergency = (c.escalationPathways ?? []).some(p => p.urgency === "emergency");
                const guidanceHref = `/nurse/conditions/${c.id}?tab=nurse-guidance`;
                return (
                <Link key={c.id} href={`/nurse/conditions/${c.id}`}>
                  <Card className="h-full hover:border-teal-500 hover:shadow-md transition-all cursor-pointer group">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <CardTitle className="text-xl group-hover:text-teal-700 transition-colors"><Highlight text={c.name} query={search} className="text-teal-700" /></CardTitle>
                        {(hasEmergency || redFlagCount > 0) && (
                          <div className="flex flex-wrap gap-1.5 shrink-0">
                            {hasEmergency && (
                              <button
                                type="button"
                                onClick={e => { e.preventDefault(); e.stopPropagation(); setLocation(guidanceHref); }}
                                aria-label="View emergency escalation guidance"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-600 text-white border border-red-700 hover:bg-red-700 transition-colors"
                              >
                                <Zap className="h-3 w-3" /> Emergency
                              </button>
                            )}
                            {redFlagCount > 0 && (
                              <button
                                type="button"
                                onClick={e => { e.preventDefault(); e.stopPropagation(); setLocation(guidanceHref); }}
                                aria-label={`View ${redFlagCount} red flag${redFlagCount === 1 ? "" : "s"}`}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors"
                              >
                                <AlertTriangle className="h-3 w-3" /> {redFlagCount} Red Flag{redFlagCount === 1 ? "" : "s"}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {c.simpleName && c.simpleName !== c.name && (
                        <p className="text-sm text-slate-500 italic"><Highlight text={c.simpleName} query={search} className="text-teal-700" /></p>
                      )}
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-4"><Highlight text={c.nurseDescription ?? ""} query={search} className="text-teal-700" /></p>
                      {c.keySymptoms && c.keySymptoms.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {c.keySymptoms.slice(0, 3).map(s => (
                            <span key={s} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">{s}</span>
                          ))}
                          {c.keySymptoms.length > 3 && (
                            <span className="px-2 py-0.5 bg-slate-50 text-slate-500 rounded text-xs">+{c.keySymptoms.length - 3} more</span>
                          )}
                        </div>
                      )}
                      <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-teal-600 font-medium">
                        <span>View full clinical guide</span>
                        <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </NurseLayout>
  );
}
